import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Round } from '@flugwettrennen/shared';
import { api, ApiError } from '../api';
import { dateTime, humanDuration } from '../lib/format';
import { goalLabel } from '../lib/goals';

export function DirectoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rounds, setRounds] = useState<Round[] | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    api.rounds.list().then((r) => setRounds(r.rounds)).catch(() => setRounds([]));
  }, []);

  async function join(e: React.FormEvent) {
    e.preventDefault();
    setJoinError(null);
    setJoining(true);
    try {
      const res = await api.rounds.join(joinCode.trim());
      navigate(`/round/${res.round.id}`);
    } catch (err) {
      setJoinError(err instanceof ApiError ? t([`challenge.notFound`, 'errors.generic']) : t('errors.generic'));
    } finally {
      setJoining(false);
    }
  }

  return (
    <div className="container page stack">
      <div className="row">
        <div>
          <div className="eyebrow">{t('common.appName')}</div>
          <h1>{t('directory.title')}</h1>
          <p className="muted" style={{ marginTop: -4 }}>{t('directory.subtitle')}</p>
        </div>
        <div className="spacer" />
        <Link className="btn btn-primary" to="/new">{t('nav.newRound')}</Link>
      </div>

      <form className="card row" onSubmit={join} style={{ gap: 10, alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="joincode">{t('challenge.joinTitle')}</label>
          <input
            id="joincode"
            className="mono"
            placeholder={t('challenge.enterCode')}
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            data-testid="join-code"
          />
          <span className="hint">{t('challenge.joinHint')}</span>
        </div>
        <button className="btn" type="submit" disabled={joining || joinCode.trim().length < 4} data-testid="join-submit">
          {t('challenge.join')}
        </button>
        {joinError && <span className="notice notice-error" style={{ padding: '6px 10px' }}>{joinError}</span>}
      </form>

      {rounds === null && <div className="spinner" />}

      {rounds && rounds.length === 0 && (
        <div className="card center stack" style={{ alignItems: 'center', padding: 48 }}>
          <p className="muted">{t('directory.empty')}</p>
          <Link className="btn btn-go" to="/new">{t('directory.createFirst')}</Link>
        </div>
      )}

      {rounds && rounds.length > 0 && (
        <div className="round-grid">
          {rounds.map((r) => {
            const dur = humanDuration(r.totalDurationMinutes);
            const target = r.status === 'finished' ? `/result/${r.id}` : `/round/${r.id}`;
            return (
              <Link key={r.id} to={target} className="card round-card" data-testid="round-card">
                <div className="top">
                  <div>
                    <div className="tag">{goalLabel(t, r.goalTypes)}</div>
                    <h3 style={{ margin: '2px 0 0' }}>{r.name}</h3>
                  </div>
                  <span className={`pill ${r.status === 'finished' ? 'pill-go' : 'pill-neutral'}`}>
                    {t(`directory.status.${r.status}`)}
                  </span>
                </div>
                <div className="muted mono" style={{ fontSize: '0.82rem' }}>
                  {r.startAirportIata} → {r.destAirportIata} · {dateTime(r.startDateTime)}
                </div>
                <div className="tag" style={{ fontSize: '0.7rem' }}>{t('challenge.code')}: {r.challengeCode}</div>
                {r.status === 'finished' && (
                  <div className="mono" style={{ fontSize: '0.85rem' }}>
                    ⏱ {t('common.duration', { h: dur.h, m: dur.m })}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
